import React from "react";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { Row, Col, Button, Card, Label } from "reactstrap";
import { InputText } from "primereact/inputtext";
import EditorContainer from "../../components/EditorContainer";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { createMarketing, updateMarketing, getMarketing } from "../../api/marketing";
import { getTenant } from "../../api/tenants";
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import { getMakes, getModels, getPreparations } from "../../api/vehicles";
import useDebounce from "../../utils/debounceHook";
import moment from "moment";
import EmailEditor from '../../components/EmailsEditor';
import CampaignMatch from "../../components/CampaignMatch"
import { it } from "../../utils/content";
import { canUserSave } from "../../auth/Can";
import roles from "../../auth/roles";

const schema = () =>
    Yup.object({
        startdate: Yup.date().required('Inserire la data inizio della campagna').nullable().when('enddate', {
            is: v => v != null,
            then: s => s.max(Yup.ref('enddate'), `La data inizio deve essere minore della data fine`),
        }),
        enddate: Yup.date().required('Inserire la data inizio della campagna').min(Yup.ref('startdate'), `La data fine deve essere maggiore della data inizio`).nullable(),
        text: Yup.string().required('Testo della campagna'),
        attributes: Yup.object({
            type: Yup.string(),
            supplier: Yup.string(),
            infocarMake: Yup.string(),
            infocarModel: Yup.string(),
            infocarSupplier: Yup.string(),
            notes: Yup.string(),
            province: Yup.string(),
            notes: Yup.string(),
            preparation: Yup.string(),
            make: Yup.string(),
            model: Yup.string(),
            kmFrom: Yup.number().typeError(""),
            kmTo: Yup.number().typeError(""),
            description: Yup.string().required('Inserire il titolo della campagna'),
        })
    });

const MarketingEditor = ({ activeTenant, readOnly }) => {

    const { id } = useParams();
    const { domains, geo } = useAuth0();
    const [tenant, setTenant] = React.useState(activeTenant);
    const [modSvc, setModSvc] = React.useState();
    const [emailBuilder, setEmailBuilder] = React.useState(false);

    const [selectedSupplier, setSelectedSupplier] = React.useState(
        modSvc && modSvc.attributes
            ? modSvc.attributes.infocarSupplier
            : undefined)
    const [selectedMake, setSelectedMake] = React.useState(
        modSvc && modSvc.attributes ? modSvc.attributes.infocarMake : undefined);
    const [selectedModel, setSelectedModel] = React.useState(
        modSvc && modSvc.attributes ? modSvc.attributes.infocarModel : undefined);

    const debouncedSupplier = useDebounce(selectedSupplier, 500);
    const debouncedMake = useDebounce(selectedMake, 500);
    const debouncedModel = useDebounce(selectedModel, 500);
    const [makes, setMakes] = React.useState([]);
    const [models, setModels] = React.useState([]);
    const [preparations, setPreparations] = React.useState([]);

    const getDDTextInEvent = e =>
        e.originalEvent && e.originalEvent.currentTarget
            ? e.originalEvent.currentTarget.innerText
            : e.value;

    React.useEffect(() => {
        loadMakes();
    }, []);

    React.useEffect(() => {
        if (debouncedSupplier && debouncedMake) {
            loadModels(debouncedSupplier, debouncedMake);
        }
    }, [debouncedSupplier, debouncedMake]);

    React.useEffect(() => {
        if (debouncedModel) {
            loadPreparations(debouncedModel);
        }
    }, [debouncedModel]);

    async function loadMakes() {
        const makesList = await getMakes();
        setMakes(makesList);
    }

    async function loadModels(alimen, make) {
        const modelsList = await getModels(make, alimen || "0");

        setPreparations([]);
        setModels(modelsList);
    }

    async function loadPreparations(modello) {
        const modelsList = await getPreparations(modello);

        setPreparations(modelsList);
    }

    const onCreate = async entity => {
        const result = await createMarketing({
            ...entity,
            tenant_id: activeTenant._id
        });
        return result;
    };

    const onLoad = async id => {
        const model = await getMarketing(id);
        const tenantDett = await getTenant(model.tenant_id);
        setModSvc(model);
        setTenant(tenantDett);
        return model;
    };

    const onUpdate = async entity => {
        const result = await updateMarketing(entity);
        return result;
    };

    const onAttributesChange = (
        changedProps,
        attributes,
        containerHandleChange
    ) => {
        const newAttributes = { ...attributes, ...changedProps };
        containerHandleChange("attributes", { target: { value: newAttributes } });
    };

    readOnly = emailBuilder;

    return (
        <EditorContainer
            key={id ? `${id}-mktEditor` : "new-mkt"}
            canSave={canUserSave([roles.ADMIN, roles.MARKETING], activeTenant)}
            readOnly={readOnly}
            schema={schema}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onLoad={onLoad}
            optionalNav={{ link: `/tenant/marketing`, text: "Campagne Marketing" }}
            redirRoot={`/tenant/marketing`}
            title={el =>
                `${el.attributes ? "Campagna Marketing: " + el.attributes.description : "NUOVA CAMPAGNA MARKETING"}`
            }
            children={(frmProps, containerHandleChange) => {
                const attributes = frmProps.values["attributes"] || {
                    model: "",
                    type: "",
                    supplier: "",
                    description: "",
                    make: "",
                    duration: "",
                    kmFrom: "",
                    kmTo: "",
                    province: "",
                    region: ""
                };

                const attributesErrors = frmProps.errors.attributes || {};
                const blocked = readOnly || (frmProps.values["status"] && frmProps.values["status"] !== "inserito");
                return (
                    <Row>
                        {!emailBuilder && (
                            <React.Fragment>
                                <Col m="12" sxs="12" md="12">
                                    <strong className="mt-2">Titolo Campagna</strong>
                                    <InputText
                                        disabled={blocked ? true : false}
                                        className={attributesErrors.description ? "p-error" : ""}
                                        value={attributes.description || ""}
                                        style={{ width: "100%" }}
                                        onChange={e =>
                                            onAttributesChange(
                                                { description: e.target.value },
                                                attributes,
                                                containerHandleChange
                                            )
                                        }
                                    />
                                    {attributesErrors.description ? (<div className="invalid-feedback">{attributesErrors.description}</div>) : null}
                                </Col>
                                <Col sm="3" xs="12">
                                    <div><strong className="mt-2">Data Inizio</strong></div>
                                    <Calendar
                                        locale={it}
                                        disabled={blocked ? true : false}
                                        monthNavigator={true}
                                        yearNavigator={true}
                                        showButtonBar={true}
                                        className={frmProps.errors.startdate ? "p-error" : ""}
                                        yearRange="1900:2099"
                                        value={
                                            frmProps.values["startdate"]
                                                ? moment(frmProps.values["startdate"]).toDate()
                                                : ""
                                        }
                                        onChange={e => containerHandleChange("startdate", e)}
                                        dateFormat="dd/mm/yy"
                                        showIcon={true}
                                    />
                                    {frmProps.errors.startdate ? (<div className="invalid-feedback">{frmProps.errors.startdate}</div>) : null}
                                </Col>
                                <Col sm="3" xs="12">
                                    <div><strong className="mt-2">Data Fine</strong></div>
                                    <Calendar
                                        locale={it}
                                        disabled={blocked ? true : false}
                                        monthNavigator={true}
                                        yearNavigator={true}
                                        showButtonBar={true}
                                        className={frmProps.errors.enddate ? "p-error" : ""}
                                        yearRange="1900:2099"
                                        value={
                                            frmProps.values["enddate"]
                                                ? moment(frmProps.values["enddate"]).toDate()
                                                : ""
                                        }
                                        onChange={e => containerHandleChange("enddate", e)}
                                        dateFormat="dd/mm/yy"
                                        showIcon={true}
                                    />
                                    {frmProps.errors.enddate ? (<div className="invalid-feedback">{frmProps.errors.enddate}</div>) : null}
                                </Col>
                                <Col sm="3" xs="12">
                                    <strong className="mt-2">Km da</strong>
                                    <InputText
                                        disabled={blocked ? true : false}
                                        className={attributesErrors.kmFrom ? "p-error" : ""}
                                        value={attributes.kmFrom || ""}
                                        style={{ width: "100%" }}
                                        keyfilter="pint"
                                        onChange={e =>
                                            onAttributesChange(
                                                { kmFrom: e.target.value },
                                                attributes,
                                                containerHandleChange
                                            )
                                        }
                                    />
                                </Col>
                                <Col sm="3" xs="12">
                                    <strong className="mt-2">Km a</strong>
                                    <InputText
                                        disabled={blocked ? true : false}
                                        className={attributesErrors.kmTo ? "p-error" : ""}
                                        value={attributes.kmTo || ""}
                                        style={{ width: "100%" }}
                                        keyfilter="pint"
                                        onChange={e =>
                                            onAttributesChange(
                                                { kmTo: e.target.value },
                                                attributes,
                                                containerHandleChange
                                            )
                                        }
                                    />
                                </Col>
                                <Col sm="6" xs="12">
                                    <strong className="mt-2">Tipo Cliente</strong>
                                    <Dropdown
                                        filter
                                        filterBy="label"
                                        id="custType"
                                        disabled={blocked ? true : false}
                                        className={attributesErrors.customerType ? "p-error" : ""}
                                        value={attributes.customerType || ""}
                                        showClear={true}
                                        style={{ width: "100%" }}
                                        options={[{ label: 'Privato', value: 'privato' }, { label: 'Azienda', value: 'azienda' }]}
                                        onChange={e => {
                                            onAttributesChange(
                                                { customerType: e.target.value },
                                                attributes,
                                                containerHandleChange
                                            )
                                        }}
                                    />
                                </Col>
                                <Col sm="3" xs="12">
                                    <strong className="mt-2">Regione</strong>
                                    <Dropdown
                                        filter
                                        filterBy="label"
                                        id="region"
                                        disabled={blocked ? true : false}
                                        value={attributes.region || ""}
                                        showClear={true}
                                        style={{ width: "100%" }}
                                        options={geo.getRegions()}
                                        onChange={e =>
                                            onAttributesChange(
                                                { region: e.target.value, province: "" },
                                                attributes,
                                                containerHandleChange
                                            )
                                        }
                                    />
                                </Col>
                                <Col sm="3" xs="12">
                                    <strong className="mt-2">Provincia</strong>
                                    <Dropdown
                                        filter
                                        filterBy="label"
                                        id="province"
                                        disabled={blocked ? true : false}
                                        value={attributes.province || ""}
                                        showClear={true}
                                        style={{ width: "100%" }}
                                        options={attributes.region ? geo.getProvinces(attributes.region) : []}
                                        onChange={e =>
                                            onAttributesChange(
                                                { province: e.target.value },
                                                attributes,
                                                containerHandleChange
                                            )
                                        }
                                    />
                                </Col>

                                <Col sm="3" xs="12">
                                    <strong className="mt-2">Tipo Veicolo</strong>
                                    <Dropdown
                                        filter
                                        filterBy="label"
                                        id="vehicleType"
                                        disabled={blocked ? true : false}
                                        className={attributesErrors.type ? "p-error" : ""}
                                        value={attributes.type || ""}
                                        showClear={true}
                                        style={{ width: "100%" }}
                                        options={domains.vehicleTypes}
                                        onChange={e => {
                                            onAttributesChange({
                                                type: e.value,
                                                model: "",
                                                infocarModel: "",
                                                preparation: "",
                                                make: "",
                                                infocarMake: ""
                                            }, attributes,
                                                containerHandleChange);
                                        }}
                                    />
                                </Col>
                                <Col sm="3" xs="12">
                                    <strong className="mt-2">Alimentazione</strong>
                                    <Dropdown
                                        filter
                                        filterBy="label"
                                        id="codSupplier"
                                        disabled={blocked ? true : false}
                                        className={attributesErrors.infocarSupplier ? "p-error" : ""}
                                        value={attributes.infocarSupplier || ""}
                                        showClear={true}
                                        style={{ width: "100%" }}
                                        options={domains.alimentazione.filter(el => el.value !== "0")}
                                        onChange={e => {
                                            onAttributesChange({
                                                infocarSupplier: e.value,
                                                supplier: getDDTextInEvent(e),
                                                model: "",
                                                infocarModel: "",
                                                preparation: ""
                                            }, attributes,
                                                containerHandleChange);
                                            setSelectedSupplier(e.value)
                                        }}
                                    />
                                </Col>
                                <Col sm="3" xs="12">
                                    <strong className="mt-2">Marca</strong>
                                    <Dropdown
                                        filter
                                        editable
                                        filterBy="label"
                                        id="infocarMake"
                                        disabled={blocked ||
                                            !attributes.supplier || Number(attributes.supplier) === 0 || !attributes.type
                                        }
                                        className={attributesErrors.infocarMake ? "p-error" : ""}
                                        value={attributes.infocarMake || ""}
                                        showClear={true}
                                        style={{ width: "100%" }}
                                        options={makes.map(el => ({
                                            label: el.Descrizione,
                                            value: el.CodiceMarca
                                        }))}
                                        onChange={e => {
                                            onAttributesChange({
                                                infocarMake: e.value,
                                                make: getDDTextInEvent(e),
                                                model: "",
                                                infocarModel: "",
                                                preparation: ""
                                            }, attributes,
                                                containerHandleChange);
                                            setSelectedMake(e.value)
                                        }}
                                    />
                                </Col>
                                <Col sm="3" xs="12">
                                    <strong className="mt-2">Modello</strong>
                                    <Dropdown
                                        filter
                                        editable
                                        filterBy="label"
                                        id="infocarModel"
                                        disabled={blocked ? true : false}
                                        className={attributesErrors.infocarModel ? "p-error" : ""}
                                        value={attributes.infocarModel || ""}
                                        showClear={true}
                                        style={{ width: "100%" }}
                                        options={models.map(el => ({
                                            label: el.Descrizione,
                                            value: el.CodiceModello
                                        }))}
                                        onChange={e => {
                                            onAttributesChange({
                                                infocarModel: e.value,
                                                model: getDDTextInEvent(e),
                                                preparation: ""
                                            }, attributes,
                                                containerHandleChange);
                                            setSelectedModel(e.value)
                                        }}
                                    />
                                </Col>
                                <Col sm="3" xs="12">
                                    <strong className="mt-2">Allestimento</strong>
                                    <Dropdown
                                        filter
                                        editable
                                        filterBy="label"
                                        id="ddpreparation"
                                        disabled={blocked ? true : false}
                                        className={attributesErrors.preparation ? "p-error" : ""}
                                        value={attributes.preparation || ""}
                                        showClear={true}
                                        style={{ width: "100%" }}
                                        options={preparations.map(el => ({
                                            label: el.DescrizioneCompleta,
                                            value: el.DescrizioneCompleta
                                        }))}
                                        onChange={e => {
                                            onAttributesChange({
                                                preparation: getDDTextInEvent(e)
                                            }, attributes,
                                                containerHandleChange);
                                        }}
                                    />
                                </Col>
                            </React.Fragment>
                        )}
                        <Col m="12" sxs="12" md="12" className="mt-2">
                            {!emailBuilder && (
                                <Button color="primary" className="mt-2" onClick={() => setEmailBuilder(true)}> CREA/MODIFICA LA EMAIL PER LA CAMPAGNA</Button>
                            )}
                            {emailBuilder && <div className="mailBuilderContainer" /* style={{ position: "fixed", left: 0, right: 0, top: 0, bottom: 0, zIndex: 300 }} */>
                                <EmailEditor
                                    onClose={() => setEmailBuilder(false)}
                                    initialValue={frmProps.values.design}
                                    projectId="4831"
                                    templateId={tenant.marketingId}
                                    onSave={(design, html) => {
                                        containerHandleChange("design", { target: { value: design } })
                                        containerHandleChange("text", { target: { value: html } })
                                        setEmailBuilder(false)
                                    }}
                                />
                            </div>}
                            {frmProps.errors.text ? (<div className="invalid-feedback">{frmProps.errors.text}</div>) : null}
                        </Col>
                        {!blocked && !emailBuilder && <CampaignMatch campaign={{ ...frmProps.values, tenant_id: tenant ? tenant._id : "" }} />}

                    </Row>
                );
            }}
            renderDealer={() =>
                tenant ? (
                    <React.Fragment>
                        <Card body className="rounded bg-secondary mb-2">
                            <Row>
                                <Col sm="4" xs="12">
                                    <strong>Dealer: </strong>
                                    <Label className="h5">{tenant.name}</Label>
                                </Col>
                                <Col sm="4" xs="12" md="3">
                                    <strong>Telefono: </strong>
                                    <Label className="h5">{tenant.phone}</Label>
                                </Col>
                                <Col sm="4" xs="12" md="3">
                                    <strong>Email: </strong>
                                    <Label className="h5">{tenant.email}</Label>
                                </Col>
                            </Row>
                        </Card>
                    </React.Fragment>
                ) : (
                        <Spinner style={{ width: "3rem", height: "3rem" }} type="grow" />
                    )
            }
        />
    );
};
export default MarketingEditor;
